export interface CompetitionDateValue {
  startsAt?: Date;
  eventTimeZoneIdentifier?: string;
}

interface IndexedDatedItem<Element> {
  item: Element;
  index: number;
  startsAt?: number;
  dayComparison?: number;
}

interface CompetitionDateBuckets<Element> {
  current: IndexedDatedItem<Element>[];
  future: IndexedDatedItem<Element>[];
  past: IndexedDatedItem<Element>[];
  undated: IndexedDatedItem<Element>[];
}

function localDayValue(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

function eventDayValue(date: Date, timeZone?: string): number {
  if (!timeZone) return localDayValue(date);

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).formatToParts(date);
    const values = new Map(parts.map((part) => [part.type, Number(part.value)]));
    const year = values.get("year");
    const month = values.get("month");
    const day = values.get("day");
    if (year !== undefined && month !== undefined && day !== undefined) {
      return Date.UTC(year, month - 1, day);
    }
  } catch {
    // Invalid event timezones fall back to the browser calendar day.
  }

  return localDayValue(date);
}

function byStartAscending<Element>(left: IndexedDatedItem<Element>, right: IndexedDatedItem<Element>): number {
  const timeDifference = (left.startsAt ?? Number.POSITIVE_INFINITY) - (right.startsAt ?? Number.POSITIVE_INFINITY);
  return timeDifference || left.index - right.index;
}

function byStartDescending<Element>(left: IndexedDatedItem<Element>, right: IndexedDatedItem<Element>): number {
  const timeDifference = (right.startsAt ?? Number.NEGATIVE_INFINITY) - (left.startsAt ?? Number.NEGATIVE_INFINITY);
  return timeDifference || left.index - right.index;
}

const MS_PER_DAY = 86_400_000;

/**
 * Whole calendar days from `relativeTo` to the competition's start, measured in
 * the event's own timezone (browser day when none is set or it is invalid).
 * Negative = past day, 0 = today, positive = future day; `undefined` when the
 * competition is undated. Shared by the relevance ordering and the Home
 * "Next up" label so they can never disagree about which day an event is on.
 */
export function competitionDayOffset(
  competition: CompetitionDateValue | undefined,
  relativeTo: Date = new Date(),
): number | undefined {
  const startDate = competition?.startsAt;
  const startsAt = startDate?.getTime();
  if (!startDate || startsAt === undefined || !Number.isFinite(startsAt)) return undefined;

  const timeZone = competition.eventTimeZoneIdentifier;
  return Math.round((eventDayValue(startDate, timeZone) - eventDayValue(relativeTo, timeZone)) / MS_PER_DAY);
}

function competitionDateBuckets<Element>(
  items: Element[],
  competitionFor: (item: Element) => CompetitionDateValue | undefined,
  relativeTo: Date,
): CompetitionDateBuckets<Element> {
  const indexed = items.map<IndexedDatedItem<Element>>((item, index) => {
    const competition = competitionFor(item);
    const dayComparison = competitionDayOffset(competition, relativeTo);
    if (dayComparison === undefined) return { item, index };

    return { item, index, startsAt: competition?.startsAt?.getTime(), dayComparison };
  });

  return {
    current: indexed.filter((item) => item.dayComparison === 0).sort(byStartAscending),
    future: indexed.filter((item) => item.dayComparison !== undefined && item.dayComparison > 0).sort(byStartAscending),
    past: indexed.filter((item) => item.dayComparison !== undefined && item.dayComparison < 0).sort(byStartDescending),
    undated: indexed.filter((item) => item.dayComparison === undefined),
  };
}

export function orderByCompetitionRelevance<Element>(
  items: Element[],
  competitionFor: (item: Element) => CompetitionDateValue | undefined,
  relativeTo: Date = new Date(),
): Element[] {
  const { current, future, past, undated } = competitionDateBuckets(items, competitionFor, relativeTo);
  const lead = current.shift() ?? future.shift();
  const remainingUpcoming = [...current, ...future].sort(byStartAscending);

  return [
    ...(lead ? [lead] : []),
    ...past,
    ...remainingUpcoming,
    ...undated,
  ].map((item) => item.item);
}

export function orderByPastCompetitionRelevance<Element>(
  items: Element[],
  competitionFor: (item: Element) => CompetitionDateValue | undefined,
  relativeTo: Date = new Date(),
): Element[] {
  const { current, future, past, undated } = competitionDateBuckets(items, competitionFor, relativeTo);
  return [...current, ...past, ...future, ...undated].map((item) => item.item);
}
